import TrackingStore from '../tracking/tracking-store';
import { getEnv } from 'mobx-easy';
import { action, computed, observable, makeObservable } from 'mobx';
import { CalculateEtaOptions } from '../../services/eta-service';
import { RootEnv } from '../../helpers/create-store';
import {
	CarArrivedData,
	CustomerFlowType,
	CustomerPickup,
	DriverActivity,
	GoogleTravelMode
} from '../../types/shared-location.types';
import { isNil as _isNil, isNumber as _isNumber, pick as _pick } from 'lodash';
import * as dayjs from 'dayjs';
import { Vehicle } from '@bringg/types';

type FormConfig = {
	options: {
		[key: string]: dayjs.Dayjs;
	};
};

class CustomerTrackingStore extends TrackingStore {
	private _formConfig: FormConfig = this.getArriveAtFormConfig();

	private _customerPickup: CustomerPickup = null;

	private _hasArrived: boolean = null;

	private _driverActivity: DriverActivity;

	vehicles: Vehicle[];

	constructor() {
		super();

		makeObservable<CustomerTrackingStore, '_formConfig' | '_customerPickup' | '_hasArrived' | '_driverActivity'>(
			this,
			{
				_formConfig: observable,
				_customerPickup: observable,
				_hasArrived: observable,
				_driverActivity: observable,
				vehicles: observable,
				setFormConfig: action,
				setCustomerPickup: action,
				setArrived: action,
				setDriverActivity: action,
				setVehicles: action,
				customerPickup: computed,
				hasArrived: computed,
				driverActivity: computed,
				formConfig: computed,
				enableCarArrivedFlow: computed,
				isCurbsideFlow: computed,
				fetchVehicles: action,
				updateEta: action,
				checkIn: action,
				isLocationAllowed: action,
				calculateEta: action
			}
		);
	}

	setFormConfig(fromConfig: FormConfig) {
		this._formConfig = fromConfig;
	}

	setCustomerPickup(customerPickup: CustomerPickup) {
		this._customerPickup = customerPickup;
	}

	setArrived(hasArrived: boolean) {
		this._hasArrived = hasArrived;
	}

	setDriverActivity(driverActivity: DriverActivity) {
		this._driverActivity = driverActivity;
	}

	setVehicles(vehicles: Vehicle[]) {
		this.vehicles = vehicles;
	}

	get customerPickup() {
		if (!_isNil(this._customerPickup)) {
			return this._customerPickup;
		}

		return this.customerExperience.customer_pickup;
	}

	get hasArrived() {
		if (!_isNil(this._hasArrived)) {
			return this._hasArrived;
		}

		return this.customerExperience.arrived;
	}

	get driverActivity() {
		if (!_isNil(this._driverActivity)) {
			return this._driverActivity;
		}

		return this.customerExperience.driver_activity;
	}

	get formConfig() {
		return this._formConfig;
	}

	get enableCarArrivedFlow() {
		return this.customerExperience.enable_vehicle_details_flow;
	}

	get isCurbsideFlow() {
		return this.customerExperience.is_curbside_flow;
	}

	get curbsideFlowType() {
		return this.customerExperience.curbside_flow_type;
	}

	get customerFlowType() {
		return this.customerExperience.customer_flow_type;
	}

	async fetchVehicles() {
		if (!this.vehicles) {
			const { sharedLocationService } = getEnv<RootEnv>();
			const res = await sharedLocationService.getVehicles(this.customerExperience.uuid);
			this.setVehicles(res || []);
		}
	}

	async updateEta(eta: dayjs.Dayjs, activity?: DriverActivity) {
		if (activity) {
			this.setDriverActivity(activity);
		}

		const { sharedLocationService } = getEnv<RootEnv>();
		const res = await sharedLocationService.postEta(
			eta,
			activity,
			this.customerExperience.uuid,
			this.customerExperience.action_tokens.update_eta
		);
		this.customerExperience.eta = eta.toISOString();
		return res;
	}

	async checkIn(customerFlowType: CustomerFlowType, carArrivedData?: CarArrivedData): Promise<void> {
		const { sharedLocationService } = getEnv<RootEnv>();

		let vehicle;

		if (carArrivedData && _isNumber(carArrivedData.id)) {
			vehicle = _pick(carArrivedData, ['id', 'parking_spot']);
		} else {
			vehicle = carArrivedData;
		}

		const { color, number } = await sharedLocationService.customerCheckIn(
			this.customerExperience.uuid,
			this.customerExperience.action_tokens.checkin,
			customerFlowType,
			vehicle
		);

		this.setCustomerPickup({ color, number, vehicle: carArrivedData });
		this.setArrived(true);
	}

	async checkOut() {
		const { sharedLocationService } = getEnv<RootEnv>();
		return sharedLocationService.customerCheckOut(
			this.customerExperience.uuid,
			this.customerExperience.action_tokens.checkout
		);
	}

	getEtaParams(origin, travelMode): CalculateEtaOptions {
		return {
			origin: origin,
			destination: this.taskLocation,
			destinationAddress: this.customerExperience.destination,
			travelMode
		};
	}

	getArriveAtFormConfig() {
		const minutesToOffer = [10, 20, 30, 40];
		const etas = minutesToOffer.reduce((etas, minutes) => {
			etas[minutes] = dayjs().add(minutes, 'minute');
			return etas;
		}, {});

		return {
			options: etas
		};
	}

	async isLocationAllowed(): Promise<boolean> {
		const { userLocationService } = getEnv<RootEnv>();
		return userLocationService.isLocationAllowed();
	}

	async calculateEta(travelMode: GoogleTravelMode): Promise<{ duration: string; distance: string }> {
		const { etaService, userLocationService } = getEnv<RootEnv>();
		const location = await userLocationService.getCurrentLocation();
		const origin = { lat: location.coords.latitude, lng: location.coords.longitude };
		const eta = await etaService.calculateEta(this.getEtaParams(origin, travelMode));

		return eta;
	}

	initialize() {
		super.initialize();
	}

	dispose = () => {
		super.dispose();
	};
}

export default CustomerTrackingStore;
